import {
  Key,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BankOutlined,
  CloseOutlined,
  ContactsOutlined,
  EyeOutlined,
  FolderOutlined,
  GlobalOutlined,
  InboxOutlined,
  LinkOutlined,
  MailOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
  TeamOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Input, Modal, Table, Tabs, Tag } from 'antd';
import type {
  FilterValue,
  SorterResult,
  TableRowSelection,
} from 'antd/es/table/interface';
import type { TablePaginationConfig } from 'antd/es/table';
import type { ColumnsType } from 'antd/es/table';
import { useProfile } from '../../profile/components/ProfileContext';
import { ClientId as ModelClient } from '../models/clients.model';
import { ClientsService } from '../services/clients.service';
import { UtilService } from '../../../../../core/services/util.service';
import { route } from '../../../../../constants/routes';

const clientInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'CL';

const websiteUrl = (website: string) => {
  const trimmed = website?.trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const websiteLabel = (website: string) =>
  website
    ?.replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/$/, '') || 'Website not provided';

const ClientsTable = forwardRef<{ refresh: () => Promise<void> }>((_, ref) => {
  const { companyId, isAdmin } = useProfile();
  const navigate = useNavigate();
  const [activeData, setActiveData] = useState<ModelClient[]>([]);
  const [archivedData, setArchivedData] = useState<ModelClient[]>([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [activeTotal, setActiveTotal] = useState(0);
  const [archivedTotal, setArchivedTotal] = useState(0);
  const [filter, setFilter] = useState<string>();
  const [sort, setSort] = useState<string>();
  const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<ModelClient | null>(
    null
  );
  const [selectedClientIds, setSelectedClientIds] = useState<Key[]>([]);
  const countsFetchedRef = useRef(false);
  const didInitialLoad = useRef(false);

  const fetchTabData = useCallback(
    async (tabType: 'active' | 'archived', fetchDataToo: boolean = true) => {
      if (!companyId) return;

      const deletedFilter =
        tabType === 'archived' ? 'deleted:eq:true' : 'deleted:eq:false';
      const combinedFilter = filter
        ? `${filter},${deletedFilter}`
        : deletedFilter;

      if (fetchDataToo) setLoading(true);
      try {
        const params = fetchDataToo
          ? {
              page: pagination.current,
              size: pagination.pageSize,
              ...(sort ? { sort } : {}),
              filter: combinedFilter,
            }
          : {
              page: 1,
              size: 1,
              filter: deletedFilter,
            };

        const result = await ClientsService.GetClients(companyId, params);
        const content = result?.content || [];
        const total = result?.totalElements || 0;

        if (fetchDataToo) {
          if (tabType === 'active') {
            setActiveData(content);
            setActiveTotal(total);
          } else {
            setArchivedData(content);
            setArchivedTotal(total);
          }
        } else if (tabType === 'active') {
          setActiveTotal(total);
        } else {
          setArchivedTotal(total);
        }
      } catch (error) {
        console.error(`Failed to fetch ${tabType} clients:`, error);
        UtilService.Alert(
          'Clients unavailable',
          'error',
          'We could not load the client directory. Please try again.'
        );
      } finally {
        if (fetchDataToo) setLoading(false);
      }
    },
    [companyId, filter, pagination, sort]
  );

  const fetchInitialData = useCallback(async () => {
    if (!companyId || countsFetchedRef.current) return;
    await Promise.all([
      fetchTabData('active', true),
      fetchTabData('archived', false),
    ]);
    countsFetchedRef.current = true;
  }, [companyId, fetchTabData]);

  useEffect(() => {
    if (companyId && !countsFetchedRef.current && !didInitialLoad.current) {
      void fetchInitialData();
      didInitialLoad.current = true;
    }
  }, [companyId, fetchInitialData]);

  useEffect(() => {
    if (didInitialLoad.current && companyId && countsFetchedRef.current) {
      void fetchTabData(activeTab, true);
    }
  }, [activeTab, companyId, fetchTabData, filter, sort]);

  useImperativeHandle(ref, () => ({
    refresh: async () => {
      setPagination({ current: 1, pageSize: 10 });
      setSort(undefined);
      setFilter(undefined);
      setSearchTerm('');
      setSelectedClient(null);
      setSelectedClientIds([]);
      countsFetchedRef.current = false;
      didInitialLoad.current = false;
      await fetchInitialData();
      didInitialLoad.current = true;
    },
  }));

  const getCurrentData = useCallback(
    () => (activeTab === 'active' ? activeData : archivedData),
    [activeData, activeTab, archivedData]
  );

  const visibleData = useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase();
    if (!normalized) return getCurrentData();
    return getCurrentData().filter((client) =>
      [
        client.name,
        client.primaryContact,
        client.primaryEmail,
        client.primaryPhoneNumber,
        client.website,
      ].some((value) => value?.toLowerCase().includes(normalized))
    );
  }, [getCurrentData, searchTerm]);

  const activePageReachable = activeData.filter(
    (client) => client.primaryEmail || client.primaryPhoneNumber
  ).length;

  const addClient = () => navigate(route('/client-profile'));

  const viewClient = (client: ModelClient) => {
    navigate(route(`/client-profile/${client.id}`), {
      state: {
        clientName: client.name,
        deleted: activeTab === 'archived',
      },
    });
  };

  const archiveClient = async (client: ModelClient) => {
    if (!companyId) return;
    try {
      await ClientsService.Delete(companyId, client.id);
      UtilService.Alert(
        'Client archived',
        'success',
        `"${client.name}" has been moved to Archived.`,
        undefined,
        async () => {
          setSelectedClient(null);
          setSelectedClientIds([]);
          await Promise.all([
            fetchTabData('active', true),
            fetchTabData('archived', false),
          ]);
        }
      );
    } catch (error) {
      console.error('Error archiving client:', error);
      UtilService.Alert(
        'Archive unavailable',
        'error',
        'The client could not be archived. Please try again.'
      );
    }
  };

  const confirmArchive = (client: ModelClient) => {
    Modal.confirm({
      title: `Archive ${client.name}?`,
      content:
        'The client will leave the active directory and remain available under Archived.',
      okText: 'Archive client',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      onOk: () => archiveClient(client),
    });
  };

  const onChange = (
    paginationConfig: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ModelClient> | SorterResult<ModelClient>[]
  ) => {
    const filterParts = Object.entries(filters).flatMap(([key, values]) => {
      const value = values?.[0];
      return value ? [`${key}:like:${value}`] : [];
    });
    setFilter(filterParts.length ? filterParts.join(',') : undefined);

    if (!Array.isArray(sorter) && sorter.order) {
      const direction = sorter.order === 'ascend' ? '' : '-';
      setSort(`${direction}${sorter.field}`);
    } else {
      setSort(undefined);
    }

    setPagination({
      current: paginationConfig.current || 1,
      pageSize: paginationConfig.pageSize || 10,
    });
  };

  const columns: ColumnsType<ModelClient> = [
    {
      title: 'Client',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      width: 220,
      render: (_, client) => (
        <div className="company-directory__identity-cell">
          <span className="company-directory__avatar" aria-hidden>
            {clientInitials(client.name)}
          </span>
          <span>
            <strong>{client.name || 'Unnamed client'}</strong>
            <small>{websiteLabel(client.website)}</small>
          </span>
        </div>
      ),
    },
    {
      title: 'Primary contact',
      dataIndex: 'primaryContact',
      key: 'primaryContact',
      sorter: true,
      width: 220,
      render: (_, client) => (
        <div className="company-directory__stacked-cell">
          <strong>{client.primaryContact || 'Not assigned'}</strong>
          <small>{client.primaryEmail || 'Email not provided'}</small>
        </div>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      width: 170,
      render: (_, client) => (
        <div className="company-directory__contact-links">
          {client.primaryEmail ? (
            <a href={`mailto:${client.primaryEmail}`}>
              <MailOutlined aria-hidden />
              Email
            </a>
          ) : (
            <span className="company-directory__muted">No email</span>
          )}
          {client.primaryPhoneNumber ? (
            <a
              href={`https://wa.me/${client.primaryPhoneNumber.replace(
                /\D/g,
                ''
              )}`}
              target="_blank"
              rel="noreferrer"
            >
              <WhatsAppOutlined aria-hidden />
              WhatsApp
            </a>
          ) : null}
        </div>
      ),
    },
    {
      title: 'Currency',
      dataIndex: 'currency',
      key: 'currency',
      width: 100,
      render: (currency: string) => currency || '—',
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, client) => (
        <Tag
          className={`company-directory__status company-directory__status--${
            client.deleted ? 'archived' : client.active ? 'active' : 'attention'
          }`}
        >
          {client.deleted ? 'Archived' : client.active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'right',
      width: 90,
      render: (_, client) => {
        const items = [
          {
            key: 'view',
            icon: <EyeOutlined />,
            label: 'View profile',
          },
          ...(isAdmin && activeTab === 'active'
            ? [
                {
                  key: 'archive',
                  icon: <FolderOutlined />,
                  label: 'Archive client',
                  danger: true,
                },
              ]
            : []),
        ];
        return (
          <Dropdown
            trigger={['click']}
            menu={{
              items,
              onClick: ({ key, domEvent }) => {
                domEvent.stopPropagation();
                if (key === 'view') viewClient(client);
                if (key === 'archive') confirmArchive(client);
              },
            }}
          >
            <Button
              type="text"
              className="company-directory__more"
              aria-label={`Actions for ${client.name}`}
              icon={<MoreOutlined />}
              onClick={(event) => event.stopPropagation()}
            />
          </Dropdown>
        );
      },
    },
  ];

  const rowSelection: TableRowSelection<ModelClient> = {
    selectedRowKeys: selectedClientIds,
    onChange: setSelectedClientIds,
    preserveSelectedRowKeys: true,
  };

  const selectedClients = getCurrentData().filter((client) =>
    selectedClientIds.includes(client.id)
  );
  const selectedEmails = selectedClients
    .map((client) => client.primaryEmail)
    .filter(Boolean)
    .join(',');

  const renderDirectory = (label: string) => (
    <section
      className="company-directory__workspace"
      aria-label={`${label} clients`}
    >
      <div className="company-directory__toolbar">
        <Input
          allowClear
          prefix={<SearchOutlined aria-hidden />}
          placeholder="Search this page by client or contact"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        <span className="company-directory__result-count" aria-live="polite">
          {visibleData.length} shown
        </span>
      </div>

      <Table<ModelClient>
        className="company-directory__table"
        columns={columns}
        dataSource={visibleData}
        rowKey="id"
        rowSelection={rowSelection}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: activeTab === 'active' ? activeTotal : archivedTotal,
          showSizeChanger: true,
          showTotal: (total) => `${total} clients`,
          onChange: (current, pageSize) => setPagination({ current, pageSize }),
          onShowSizeChange: (current, pageSize) =>
            setPagination({ current, pageSize }),
        }}
        onChange={onChange}
        onRow={(client) => ({
          onClick: () => setSelectedClient(client),
        })}
        rowClassName={(client) =>
          selectedClient?.id === client.id
            ? 'company-directory__row--previewed'
            : ''
        }
        scroll={{ x: 920 }}
      />

      {selectedClientIds.length ? (
        <div className="company-directory__bulk" role="status">
          <strong>
            {selectedClientIds.length} client
            {selectedClientIds.length === 1 ? '' : 's'} selected
          </strong>
          {selectedEmails ? (
            <a href={`mailto:${selectedEmails}`}>
              <MailOutlined aria-hidden />
              Email
            </a>
          ) : null}
          {selectedClients.length === 1 ? (
            <button
              type="button"
              onClick={() => viewClient(selectedClients[0])}
            >
              <EyeOutlined aria-hidden />
              Open profile
            </button>
          ) : null}
          <button type="button" onClick={() => setSelectedClientIds([])}>
            <CloseOutlined aria-hidden />
            Clear
          </button>
        </div>
      ) : null}
    </section>
  );

  const tabItems = [
    {
      key: 'active',
      label: (
        <span>
          Active <b>{activeTotal}</b>
        </span>
      ),
      children: renderDirectory('Active'),
    },
    {
      key: 'archived',
      label: (
        <span>
          Archived <b>{archivedTotal}</b>
        </span>
      ),
      children: renderDirectory('Archived'),
    },
  ];

  return (
    <div className="company-directory">
      <header className="company-directory__header">
        <div>
          <span>Client portfolio</span>
          <h2>Clients</h2>
          <p>Manage client relationships, contacts, and account status.</p>
        </div>
        {isAdmin ? (
          <button
            className="company-primary-action"
            type="button"
            onClick={addClient}
          >
            <PlusOutlined aria-hidden />
            Add client
          </button>
        ) : null}
      </header>

      <section
        className="company-directory__metrics"
        aria-label="Client totals"
      >
        <article>
          <span className="company-directory__metric-icon">
            <TeamOutlined aria-hidden />
          </span>
          <div>
            <span>Total clients</span>
            <strong>{activeTotal + archivedTotal}</strong>
          </div>
        </article>
        <article>
          <span className="company-directory__metric-icon">
            <BankOutlined aria-hidden />
          </span>
          <div>
            <span>Active</span>
            <strong>{activeTotal}</strong>
          </div>
        </article>
        <article>
          <span className="company-directory__metric-icon">
            <ContactsOutlined aria-hidden />
          </span>
          <div>
            <span>Reachable on page</span>
            <strong>{activePageReachable}</strong>
          </div>
        </article>
        <article>
          <span className="company-directory__metric-icon company-directory__metric-icon--muted">
            <InboxOutlined aria-hidden />
          </span>
          <div>
            <span>Archived</span>
            <strong>{archivedTotal}</strong>
          </div>
        </article>
      </section>

      <div
        className={`company-directory__layout${
          selectedClient ? ' company-directory__layout--preview' : ''
        }`}
      >
        <section className="company-directory__panel">
          <Tabs
            activeKey={activeTab}
            items={tabItems}
            onChange={(key) => {
              setActiveTab(key as 'active' | 'archived');
              setPagination((current) => ({ ...current, current: 1 }));
              setSearchTerm('');
              setSelectedClient(null);
              setSelectedClientIds([]);
            }}
          />
        </section>

        {selectedClient ? (
          <aside
            className="company-directory__preview"
            aria-label="Client preview"
          >
            <div className="company-directory__preview-header">
              <strong>Client preview</strong>
              <Button
                type="text"
                icon={<CloseOutlined />}
                aria-label="Close client preview"
                onClick={() => setSelectedClient(null)}
              />
            </div>
            <div className="company-directory__preview-body">
              <div className="company-directory__preview-identity">
                <span className="company-directory__preview-avatar" aria-hidden>
                  {clientInitials(selectedClient.name)}
                </span>
                <div>
                  <h3>{selectedClient.name || 'Unnamed client'}</h3>
                  {selectedClient.website ? (
                    <a
                      href={websiteUrl(selectedClient.website)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {websiteLabel(selectedClient.website)}
                    </a>
                  ) : (
                    <span>Website not provided</span>
                  )}
                  <Tag
                    className={`company-directory__status company-directory__status--${
                      selectedClient.deleted
                        ? 'archived'
                        : selectedClient.active
                          ? 'active'
                          : 'attention'
                    }`}
                  >
                    {selectedClient.deleted
                      ? 'Archived'
                      : selectedClient.active
                        ? 'Active account'
                        : 'Inactive'}
                  </Tag>
                </div>
              </div>

              <section className="company-directory__preview-section">
                <h4>Primary contact</h4>
                <strong>
                  {selectedClient.primaryContact || 'No contact assigned'}
                </strong>
                <span>
                  {selectedClient.primaryEmail || 'Email not provided'}
                </span>
                <span>
                  {selectedClient.primaryPhoneNumber || 'Phone not provided'}
                </span>
              </section>

              <div className="company-directory__preview-actions">
                {selectedClient.primaryEmail ? (
                  <a href={`mailto:${selectedClient.primaryEmail}`}>
                    <MailOutlined aria-hidden />
                    Email
                  </a>
                ) : null}
                {selectedClient.primaryPhoneNumber ? (
                  <a
                    href={`https://wa.me/${selectedClient.primaryPhoneNumber.replace(
                      /\D/g,
                      ''
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <WhatsAppOutlined aria-hidden />
                    WhatsApp
                  </a>
                ) : null}
                {selectedClient.website ? (
                  <a
                    className="company-directory__preview-action--wide"
                    href={websiteUrl(selectedClient.website)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <GlobalOutlined aria-hidden />
                    Open website
                    <LinkOutlined aria-hidden />
                  </a>
                ) : null}
              </div>

              <section className="company-directory__preview-section">
                <h4>Account details</h4>
                <dl className="company-directory__details">
                  <div>
                    <dt>Currency</dt>
                    <dd>{selectedClient.currency || 'Not configured'}</dd>
                  </div>
                  <div>
                    <dt>Directory</dt>
                    <dd>{selectedClient.deleted ? 'Archived' : 'Active'}</dd>
                  </div>
                </dl>
              </section>
            </div>
            <button
              className="company-primary-action company-directory__preview-primary"
              type="button"
              onClick={() => viewClient(selectedClient)}
            >
              View client profile
            </button>
          </aside>
        ) : null}
      </div>
    </div>
  );
});

export default ClientsTable;
